<template lang="pug">
  .box
    VueApexCharts(
      type="bar"
      height="350"
      :options="chartOptions"
      :series="series"
    )

    .details
      .title Detaylar ({{ activeDateRange[2] }})
      .loading(v-if="loading")
        Loading
      .tabs(v-else-if="detail && detail.length")
        .tab(v-for="[k, v] in Object.entries(paymentTypeEnumNames)"
          :class="{'active': activeType == k}"
          @click="activeType = k"
        ) {{ v }}
      .tab-detail(v-if="!loading")
        .grid(v-if="filteredDetails.length")
          template(v-for="data in filteredDetails")
            .cell.gray {{ getDate(data.from_date) }}
            .cell(:style="{color: getTypeColor(data.type)}") {{ getTypeName(data.type) }}
            .cell {{ data.merchant_licence.merchant_detail.admin_user.email }}
            .cell.gray {{ getSubName(data) }}
            .cell.right {{ getPrice(data) }}
            .cell.gray.right ({{ getFormattedPrice(data) }})
        .no-detail(v-else)
          span Gösterilecek veri yok

</template>

<script>
  import VueApexCharts from 'vue-apexcharts'
  import _sum from 'lodash/sum'
  import _sortBy from 'lodash/sortBy'
  import { roundToK } from '@/utils/helper'
  import moment from 'moment'
  import { formatMoney } from '@/utils/currency'

  import {
    PaymentTypeEnum,
    PaymentTypeEnumNames
  } from '@/model/Welcome'
  import { mapActions } from "vuex";

  export default {
    name: 'MRRChangeChart',
    components: {
      VueApexCharts
    },

    props: ['data', 'dateList'],

    data () {
      return {
        activeDateRange: null,
        detail: [],
        activeType: PaymentTypeEnum.NEW,
        loading: false
      }
    },

    watch: {
      'dateList' (val) {
        this.refreshDateRange()
      },

      activeDateRange () {
        this.loadDetail()
      }
    },

    computed: {
      paymentTypeEnumNames () {
        return PaymentTypeEnumNames
      },

      series () {
        return [
          {
            name: 'Reactivation',
            type: 'column',
            data: this.data.map(d => d.types_dict[PaymentTypeEnum.REACTIVATION].mrr.total)
          },
          {
            name: 'Expansion',
            type: 'column',
            data: this.data.map(d => d.types_dict[PaymentTypeEnum.EXPANSION].mrr.total)
          },
          {
            name: 'New',
            type: 'column',
            data: this.data.map(d => d.types_dict[PaymentTypeEnum.NEW].mrr.total)
          },
          {
            name: 'Contraction',
            type: 'column',
            data: this.data.map(d => d.types_dict[PaymentTypeEnum.CONTRACTION].mrr.total)
          },
          {
            name: 'Churn',
            type: 'column',
            data: this.data.map(d => d.types_dict[PaymentTypeEnum.CHURN].mrr.total)
          },
          {
            name: 'Net MRR',
            type: 'line',
            data: this.data.map(d => _sum(d.types.map(t => t.payment_type === PaymentTypeEnum.RENEWAL ? 0 : t.mrr.total)))
          }
        ]
      },

      chartOptions () {
        let self = this
        return {
          chart: {
            height: 350,
            type: 'bar',
            stacked: true,
            events: {
              dataPointSelection: function (e, chart, opts) {
                self.activeDateRange = self.dateList[opts.dataPointIndex + 1]
              },
            }
          },
          title: {
            text: "Yeni MRR",
            align: 'left',
          },
          labels: this.data.map(d => d.stat_date),
          stroke: {
            width: 5,
            curve: 'straight',
            dashArray: 8
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: '50%',
            }
          },
          // colors: ['#36b2d4', '#5e36e0', '#26ba4a', '#fdb444', '#dd3573', '#1f1e30'],
          colors: ['#176BA0', '#19AADE', '#6CECC1',
            '#DE542C', '#EE9A3A', '#142459'],
          fill: {
            opacity: [1, 1, 1, 1, 1, 0.70],
          },
          dataLabels: {
            enabled: true,
            enabledOnSeries: [5],
            formatter: function (val) {
              return "₺ " + roundToK(val)
            },
            background: {
              enabled: true,
              foreColor: '#142459',
              padding: 4,
              borderRadius: 2,
              borderWidth: 1,
              borderColor: '#142459',
              opacity: 1
            },
          },
          states: {
            normal: {
              filter: {
                type: 'desaturate'
              }
            },
            active: {
              allowMultipleDataPointsSelection: true,
              filter: {
                type: 'darken',
                value: 1
              }
            }
          },
          xaxis: {
            tickPlacement: 'between',
            labels: {
              rotate: -45,
              rotateAlways: true,
              hideOverlappingLabels: false,
            }
          },
          yaxis: {
            labels: {
              formatter: val => {
                return "₺ " + roundToK(val)
              },
            }
          },
          tooltip: {
            shared: true,
            followCursor: false,
            y: {
              formatter: function (y) {
                if (typeof y !== "undefined") {
                  return "₺ " + y.toFixed(2);
                }
                return y;

              }
            }
          }
        }
      },

      filteredDetails () {
        return _sortBy(this.detail.filter(d => d.type == this.activeType && d.sub_price_after_discount !== 0), 'from_date').reverse()
      }
    },

    methods: {
      ...mapActions('Welcome', [
        'fetchWelcomeDetail',
      ]),

      refreshDateRange () {
        if (this.dateList)
          this.activeDateRange = this.dateList[this.dateList.length - 1]
      },

      async loadDetail () {
        try {
          this.loading = true
          let response = await this.fetchWelcomeDetail({
            date_start: this.activeDateRange[0],
            date_end: this.activeDateRange[1]
          })

          this.detail = _sortBy(response, 'from_data')
        } catch (e) {
          console.error(e)
        } finally {
          this.loading = false
        }
      },

      getTypeColor(type) {
        switch (type) {
          case PaymentTypeEnum.NEW:
            return '#6CECC1'
            break;
          case PaymentTypeEnum.RENEWAL:
            return '#9BB1C8'
            break
          case PaymentTypeEnum.EXPANSION:
            return '#19AADE'
            break
          case PaymentTypeEnum.REACTIVATION:
            return '#176BA0'
            break
          case PaymentTypeEnum.CONTRACTION:
            return '#DE542C'
            break
          case PaymentTypeEnum.CHURN:
            return '#EE9A3A'
            break
        }
      },

      getTypeName(type) {
        return PaymentTypeEnumNames[type]
      },

      getDate(dateStr) {
        const serverDateFormat = "YYYY-MM-DD HH:mm:ss"
        return moment(dateStr, serverDateFormat).format('DD MMM \'YY')
      },

      getPrice (d) {
        let price = d.sub_price_after_discount
        if (d.renewal_period === 365) {
          price = price / 12
        }

        if (d.type === PaymentTypeEnum.CONTRACTION || d.type ===  PaymentTypeEnum.CHURN){
          return formatMoney(-1 * price)
        } else {
          return formatMoney(price)
        }
      },

      getFormattedPrice(d) {
        let price = d.paid_price
        if (d.type === PaymentTypeEnum.CONTRACTION || d.type ===  PaymentTypeEnum.CHURN){
          return formatMoney(-1 * price)
        } else {
          return formatMoney(price)
        }
      },

      getSubName(data) {
        let period = "Aylık"
        let name = data.subscription.hardware_json.tr.name
        if (data.renewal_period == 365)
          period = "Yıllık"

        if (name.includes(period))
          return name
        else
          return name + ' - ' + period
      },
    },

    created () {
      this.refreshDateRange()
    }
  }
</script>

<style lang="scss" scoped>
  /deep/ {
    .apexcharts-bar-area {
      cursor: pointer;
    }
  }
  .details {
    font-size: 13px;
    min-height: 200px;

    .title {
      font-size: 14px;
      font-weight: 900;
      margin: 20px 10px;
    }

    .loading {
      width: 100%;
      padding: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .tabs {
      display: flex;

      .tab {
        cursor: pointer;
        padding: 6px 10px;
        margin: 0 10px;
        border-bottom: 3px solid white;
        color: $color-gray-500;
        transition: all $transition-normal;

        &.active {
          color: $color-navy-500;
          border-color: $color-blue-600;
        }
      }
    }

    .grid {
      display: grid;
      grid-template-columns: auto 120px auto auto 1fr auto;
      margin: 10px 0;
      max-height: 300px;
      overflow: auto;

      .cell {
        padding: 10px;

        border-bottom: 1px solid $color-gray-200;

        &.gray {
          color: $color-gray-500;
        }

        &.right {
          text-align: right;
        }

      }
    }

    .no-detail {
      display: flex;
      align-items: center;
      justify-content: center;
      color: $color-gray-500;
      padding: 60px;
    }
  }
</style>
